import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'

import CivWallet from '../../../components/CivWallet/civ-wallet'
import SettingsCard from '../../../components/Settings/SettingsCard'
import SettingsLayout from '../../../components/Settings/SettingsLayout'
import { ROUTES } from '../../../constants/routes'
import { isCharityStripeVerified } from '../../../lib/stripeConnect'
import SocialMediaCard from '../../account/settings/SocialMediaCard'
import CharityDonationLinksCard from './CharityDonationLinksCard'
import CharityLocationsCard from './CharityLocationsCard'
import CharityLogoCard from './CharityLogoCard'
import CharityOrganizationCard from './CharityOrganizationCard'
import CharityVerificationCard from './CharityVerificationCard'
import { CHARITY_SOCIAL_FIELDS } from './constants'

export default function CharitySettingsContent({
  charity,
  charityId,
  breadcrumbs,
  error,
  setError,
  setCharity,
  locale,
  updateCharity,
  verifiedCharities,
  locations,
  clearedLocations,
  onLocationChange,
  updateCharityField,
  updateSocialField,
  onLogoChange,
  onSaveDonationLinks,
  onSaveSocials,
  onSaveLocations,
  hasDonationLinksChanged,
  hasSocialsChanged,
  hasLocationsChanged,
  saving,
  stripeReturnPath,
  t,
}) {
  const navigate = useNavigate()
  const [stripeStatus, setStripeStatus] = useState(null)
  const walletUnlocked =
    isCharityStripeVerified(stripeStatus) && !!charity?.wallet_id

  return (
    <SettingsLayout
      title={t('charities.settings.title')}
      breadcrumbs={breadcrumbs}
    >
      {verifiedCharities.length > 1 ? (
        <div className="mb-3 d-flex justify-content-end">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate(ROUTES.CHARITY_ACCOUNT)}
          >
            {t('charities.settings.switch_charity')}
          </Button>
        </div>
      ) : null}

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          className="mb-3"
        >
          {error}
        </Alert>
      )}

      <CharityVerificationCard
        charityId={charityId}
        returnPath={stripeReturnPath}
        onStatusChange={setStripeStatus}
      />

      {walletUnlocked ? (
        <SettingsCard
          id="wallet"
          icon="wallet"
          title={t('navigation_banner.civ_wallet', 'CIV Wallet')}
        >
          <CivWallet
            data={charity}
            setData={setCharity}
            locale={locale}
            updateData={updateCharity}
            origin={'charity'}
          />
        </SettingsCard>
      ) : null}

      <CharityOrganizationCard charity={charity} />

      <CharityLogoCard
        logoUrl={charity?.logo_url}
        onLogoChange={onLogoChange}
      />

      <CharityDonationLinksCard
        charity={charity}
        onFieldChange={updateCharityField}
        onSave={onSaveDonationLinks}
        hasChanged={hasDonationLinksChanged}
        isSaving={saving}
      />

      <SocialMediaCard
        data={charity}
        onFieldChange={updateSocialField}
        onSave={onSaveSocials}
        hasChanged={hasSocialsChanged}
        isSaving={saving}
        fields={CHARITY_SOCIAL_FIELDS}
        title={t('charities.settings.social_title')}
      />

      <CharityLocationsCard
        locations={locations}
        clearedLocations={clearedLocations}
        onLocationChange={onLocationChange}
        onSave={onSaveLocations}
        hasChanged={hasLocationsChanged}
        isSaving={saving}
      />
    </SettingsLayout>
  )
}

CharitySettingsContent.propTypes = {
  charity: PropTypes.object.isRequired,
  charityId: PropTypes.number.isRequired,
  breadcrumbs: PropTypes.array.isRequired,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  setCharity: PropTypes.func,
  locale: PropTypes.string,
  updateCharity: PropTypes.func,
  verifiedCharities: PropTypes.array,
  locations: PropTypes.object.isRequired,
  clearedLocations: PropTypes.object.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  updateCharityField: PropTypes.func.isRequired,
  updateSocialField: PropTypes.func.isRequired,
  onLogoChange: PropTypes.func.isRequired,
  onSaveDonationLinks: PropTypes.func.isRequired,
  onSaveSocials: PropTypes.func.isRequired,
  onSaveLocations: PropTypes.func.isRequired,
  hasDonationLinksChanged: PropTypes.bool,
  hasSocialsChanged: PropTypes.bool,
  hasLocationsChanged: PropTypes.bool,
  saving: PropTypes.bool,
  stripeReturnPath: PropTypes.string,
  t: PropTypes.func.isRequired,
}
